import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager"
import { speedFormatMPH } from 'Core/data/Helpers';
import { getApiDate } from '../../../../../../core/hooks/useUnitData';
import { orderTrailPolilyne } from '../../../../../../components/Map/utils'
import { NotificationManager } from "Components/Notifications";
import { v4 as uuidv4 } from "uuid";
import { resetALL } from "../../../../../../.globals/redux/actions";
import { createClipRequestFromDrawer } from "../../../../../../components/Map/components/ClipRequestDrawerMap/redux/slice";

const SEARCH_VEHICLE_TRAILS_QUERY = `
    query searchVehicleTrails (
        $startDate: AWSDateTime!,
        $endDate: AWSDateTime!,
        $event: VehicleTrailEvent,
        $deviceId: String!,
        $openSearch: String,
        $limit: Int,
        $offset: Int,
        $includeLinkedData: Boolean,
        $withRecentEvent: Boolean
    ) {
        Trails: searchVehicleTrails(
            startDate: $startDate,
            endDate: $endDate,
            event: $event,
            deviceId: $deviceId,
            openSearch: $openSearch,
            limit: $limit,
            offset: $offset,
            includeLinkedData: $includeLinkedData,
            withRecentEvent: $withRecentEvent
        ) {
            items {
                id
                deviceData
                deviceId
                latitude
                longitude
                heading
                eventCode
                eventName
                address
                formattedAddress
                driverId
                driverName
                ignitionStatus
                satelliteCount
                signal
                ecuSpeed
                speed
                speedLimit
                unitTime
                landmark {
                    id
                    companyId
                    name
                    tags {
                        id
                        label
                        color
                        path
                    }
                    address
                    city
                    zip
                    state
                    country
                }
                trail {
                    lat
                    lon
                    timestamp
                }
            }
            total
        }
    }
`;

const SEARCH_CLIP_REQUESTS_QUERY = `
    query searchClipRequests (
        $openSearch: String,
        $entities: [EntityInput]!,
        $hasNotes: Boolean,
        $hasStarred: Boolean,
        $isDeleted: Boolean,
        $dateRange: AWSDateTimeRange!,
        $limit: Int,
        $offset: Int,
        $status: String
    ) {
        searchClipRequests(
            openSearch: $openSearch,
            entities: $entities,
            hasNotes: $hasNotes,
            hasStarred: $hasStarred,
            isDeleted: $isDeleted,
            dateRange: $dateRange,
            limit: $limit,
            offset: $offset,
            status: $status
        ) {
            items {
                id
                accountId
                createdOn
                deviceId
                esn
                label
                status
                latitude
                longitude
                time
                tripID
                clipID
                rotate
                url
                startDate
                endDate
                notes
                starred
                errorMsg
                deviceModelId
                deviceModelName
                driverName
                driverExternalId
                thumbnailUrl
                transactionId
                details
                isDeleted
                isPrivate
                createdBy
                createdByUser {
                    authId
                    username
                }
            }
            total
        }
    }
`;


const INIT_STATE = {
    restart: false,

    //----------------------------------------------
    // Vehicle Trails state
    vehicleTrails: { items: [], total: 0},
    loadVehicleTrails: false,
        
    filters: {
      openSearch: "",
      limit: 10,
      offset: 0,
      total: 0
    },
    error: null,
    unitVehicleTrails: null,
    vehicletrailsPrint: [],
    unitToPrint: null,
    exportData: '',

    //----------------------------------------------
    //clip requests state
    clipRequests: { items: [], total: 0 },
    loadClipRequests: false,
    errorClipRequests: false,
    clipRequestsInvalidated: false,
    clipRequestsFilters: {
        openSearch: "",
        limit: 10,
        offset: 0,
        total: 0,
        resetSearch: true,
        entities: [{ name: "All", id: -1 }],
        hasStarred: false,
        hasNotes: false,
        isDeleted: false,
        status: "all",
        dateRange: null,
    },

    vehicleTrips: { items: [], total: 0},
    loadVehicleTrips: false,
    errorTrips: null,
    vehicleActivityDates: { items: [], total: 0},
    loadVehicleActivityDates: false,
    errorActivityDates: null,

    /** true cuando el usuario tiene seleccionada una carta de trip en VehicleTripsMode */
    isTripsModeActive: false,
};

const route = "modules/realtimemaps/main/components/vehicleTrails";

export const vehicleTrailsSearchTrails = createAsyncThunk('components/vehicleTrails', async ({ openSearch, limit = 10, offset = 0, paramConditions = [] }, { rejectWithValue, dispatch }) => {
    try {
        // NOTE: this thunk used to query routes; it now matches the saga getVehicleTrailsRequest
        const {
            unitId,
            event,
            startDate,
            endDate,
            useEcuSpeed,
            unitType,
            kph,
            withRecentEvent,
        } = (paramConditions && paramConditions[0]) || {};

        const queryOffset = offset == 0 ? 0 : (offset - 1) * limit;
        let queryElements = {
            deviceId: unitId,
            event,
            startDate,
            endDate,
            openSearch: openSearch ? openSearch : '',
            limit,
            offset: queryOffset,
            includeLinkedData: true,
            withRecentEvent,
        };

        if (event == '0') {
            delete queryElements["event"];
        }
        if (!openSearch) {
            delete queryElements["openSearch"];
        }

        const response = await clientQuery(
            SEARCH_VEHICLE_TRAILS_QUERY,
            queryElements,
            endpoints.GRAPHQL_GENERAL,
        );

        if (!response?.Trails) {
            throw new Error('Not Vehicle Trails');
        }

        const vehicleTrailsData = response.Trails;
        if (!vehicleTrailsData?.items) {
            return { items: [], total: 0, trailRoute: [] };
        }

        let countItems = queryOffset;
        let wayPoint = vehicleTrailsData.items.length + 1;

        // Sort asc to build trail route
        let dataTrailsAsc = [...vehicleTrailsData.items].sort((a, b) => {
            const unitTimeA = parseInt(getApiDate(a.unitTime));
            const unitTimeB = parseInt(getApiDate(b.unitTime));
            return unitTimeA - unitTimeB;
        });

        const trailRoute = orderTrailPolilyne([...vehicleTrailsData.items]);

        // Hide repeated event markers (by eventName)
        let lastEvent = '';
        let isChild = false;
        dataTrailsAsc = dataTrailsAsc.map((item) => {
            if (lastEvent != item.eventName) {
                lastEvent = item.eventName;
                isChild = false;
            } else {
                isChild = true;
            }
            return { ...item, hideMarker: isChild };
        });

        // Sort desc for display
        let dataTrails = dataTrailsAsc.sort((a, b) => {
            const unitTimeA = parseInt(getApiDate(a.unitTime));
            const unitTimeB = parseInt(getApiDate(b.unitTime));
            return unitTimeB - unitTimeA;
        });

        dataTrails = dataTrails.map((item) => {
            countItems++;
            wayPoint--;
            const speed = useEcuSpeed && item?.ecuSpeed > 0 ? item?.ecuSpeed : item.speed;
            const speedFormat = unitType && kph ? speedFormatMPH(speed, unitType, kph, true) : 0;
            return {
                ...item,
                index: countItems,
                wayPoint,
                speedFormat,
                speed,
                unitType,
            };
        });

        return { ...vehicleTrailsData, items: dataTrails, trailRoute };

    } catch (exc) {
        ExceptionManager(exc, route, 'vehicleTrailsSearchTrails');
        return rejectWithValue(exc);
    }
});

export const vehicleTrailsSearchClipRequests = createAsyncThunk(
    "components/vehicleTrails/searchClipRequests",
    async (
        {
            limit,
            dateRange,
            resetSearch,
            offset,
            entities,
            hasStarred,
            hasNotes,
            isDeleted,
            status,
            openSearch,
        },
        { rejectWithValue }
    ) => {
        try {
            const variables = {
                dateRange,
                offset,
                limit,
                entities:
                    !entities || !entities?.length || entities.find((e) => e.id == -1)
                        ? [{ name: "All", id: -1 }]
                        : entities,
                isDeleted: isDeleted || false,
                hasStarred: hasStarred || false,
                hasNotes: hasNotes || false,
                status: status || "all",
                openSearch,
            };

            const data = await clientQuery(
                SEARCH_CLIP_REQUESTS_QUERY,
                variables,
                endpoints.GRAPHQL_GENERAL
            );

            if (data?.searchClipRequests) {
                const {
                    searchClipRequests: { items, total },
                } = data;
                return {
                    items: items || [],
                    total: total || 0,
                    resetSearch: !!resetSearch,
                    openSearch,
                    error: false,
                };
            }

            throw new Error("Error while searching clip requests");
        } catch (exc) {
            ExceptionManager(exc, route, "vehicleTrailsSearchClipRequests");
            return rejectWithValue({ items: [], total: 0, resetSearch: true, error: true });
        }
    }
);
    
export const searchVehicleTrips = createAsyncThunk(
    `${route}/searchVehicleTrips`,
    async (
        { unitId, event, startDate, endDate, color, messageError },
        { rejectWithValue }
    ) => {
        let queryElements = {
            deviceId: unitId,
            event,
            startDate,
            endDate,
            limit: 100,
            offset: 0
        };
        if (event === '0') {
            delete queryElements.event;
        }

        try {
            const data = await clientQuery(
                `query searchVehicleTrips (
                    $startDate: AWSDateTime!
                    $endDate: AWSDateTime!
                    $deviceId: String!
                    $limit: Int
                    $offset: Int) 
                {
                    searchVehicleTrips (startDate: $startDate, endDate: $endDate, deviceId: $deviceId, limit: $limit, offset: $offset) {
                        items {
                            start {
                                deviceId
                                latitude
                                longitude
                                heading
                                eventCode
                                eventName
                                address
                                driverId
                                driverName
                                ignitionStatus
                                satelliteCount
                                signal
                                speed
                                speedLimit
                                unitTime
                            }
                            end {
                                deviceId
                                latitude
                                longitude
                                heading
                                eventCode
                                eventName
                                address
                                driverId
                                driverName
                                ignitionStatus
                                satelliteCount
                                signal
                                speed
                                speedLimit
                                unitTime
                            }
                        }
                        total
                    }
                }`,
                queryElements,
                endpoints.GRAPHQL_GENERAL
            );

            if (data && data.searchVehicleTrips) {
                return { data: data.searchVehicleTrips.items, total: data.searchVehicleTrips.total };
            }

            return rejectWithValue({ error: true });
        } catch (e) {
            NotificationManager.error(messageError, 'Error', 3000, null, null, 'error');
            return rejectWithValue(e);
        }
    }
);

export const searchVehicleActivityDates = createAsyncThunk(
    `${route}/searchVehicleActivityDates`,
    async (
        { unitId},
        { rejectWithValue }
    ) => {
        let queryElements = {
            deviceId: unitId,
            // 90 days ago
            startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString(),
			includeLinkedData: true
        };

        try {
            const data = await clientQuery(
                `query searchVehicleActivityDates ($startDate: AWSDateTime!, $endDate: AWSDateTime!, $deviceId: String!) {
                    searchVehicleActivityDates (startDate: $startDate, endDate: $endDate, deviceId: $deviceId) {
                        items {
                            date
                        }
                        total
                    }   
                }`,
                queryElements,
                endpoints.GRAPHQL_GENERAL
            );

            if (data && data.searchVehicleActivityDates) {
                return { data: data.searchVehicleActivityDates.items, total: data.searchVehicleActivityDates.total };
            }

            return rejectWithValue({ error: true });
        } catch (e) {
            NotificationManager.error(messageError, 'Error', 3000, null, null, 'error');
            return rejectWithValue(e);
        }
    }
);

export const VehicleTrailsSliceRedux = createSlice({
    name: route,
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        },
        setUnitVehicleTrail: (state, action) => {
            state.unitVehicleTrails = action.payload;
        },
        setTripsModeActive: (state, action) => {
            state.isTripsModeActive = action.payload;
        }
        /* ,
        searchRoutesTrackerRefresh: (state, action) => {
            return {
                ...state,
                searchRoutesTracker: action.payload
                
            }
        }, */
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);
        builder.addCase(vehicleTrailsSearchTrails.pending, (state, action) => {
            state.loadVehicleTrails = true;
        });
        builder.addCase(vehicleTrailsSearchTrails.fulfilled, (state, action) => {
            state.vehicleTrails = action?.payload;
            state.loadVehicleTrails = false;

        } );
        builder.addCase(vehicleTrailsSearchTrails.rejected, (state, action) => {  
            state.detail = [];
            state.error = action.payload;
            state.loadVehicleTrails = false;

        });

        builder.addCase(vehicleTrailsSearchClipRequests.pending, (state) => {
            state.loadClipRequests = true;
            state.errorClipRequests = false;
        });
        builder.addCase(vehicleTrailsSearchClipRequests.fulfilled, (state, action) => {
            const { items, total, resetSearch, openSearch } = action.payload || {};
            const lastParams = action.meta?.arg ?? {};

            state.clipRequests = {
                items: resetSearch ? (items || []) : [...(state.clipRequests.items || []), ...(items || [])],
                total: total ?? 0,
            };

            state.clipRequestsFilters = {
                ...state.clipRequestsFilters,
                ...lastParams,
                openSearch: openSearch ?? state.clipRequestsFilters.openSearch,
                total: total ?? state.clipRequestsFilters.total,
            };

            state.loadClipRequests = false;
            state.errorClipRequests = false;
            state.clipRequestsInvalidated = false;
        });
        builder.addCase(vehicleTrailsSearchClipRequests.rejected, (state, action) => {
            state.clipRequests = { items: [], total: 0 };
            state.loadClipRequests = false;
            state.errorClipRequests = true;
            state.error = action.payload || action.error;
        });

        builder.addCase(createClipRequestFromDrawer.fulfilled, (state) => {
            state.clipRequestsInvalidated = true;
        });

        builder.addCase(searchVehicleTrips.pending, (state) => {
            state.loadVehicleTrips = true;
        });
        builder.addCase(searchVehicleTrips.fulfilled, (state, action) => {
            state.vehicleTrips = action.payload;
            state.loadVehicleTrips = false;
        });
        builder.addCase(searchVehicleTrips.rejected, (state, action) => {
            state.vehicleTrips = { items: [], total: 0 };
            state.loadVehicleTrips = false;
            state.errorTrips = action.payload;
        });

        builder.addCase(searchVehicleActivityDates.pending, (state) => {
            state.loadVehicleActivityDates = true;
        });
        builder.addCase(searchVehicleActivityDates.fulfilled, (state, action) => {
            const result = action?.payload?.data?.map((item) => new Date(item.date));
            state.vehicleActivityDates = { items: result, total: result?.length ?? 0 };
            state.loadVehicleActivityDates = false;
        });
        builder.addCase(searchVehicleActivityDates.rejected, (state, action) => {
            state.vehicleActivityDates = { items: [], total: 0 };
            state.loadVehicleActivityDates = false;
            state.errorActivityDates = action.payload;
        });
    }
})

const { actions, reducer } = VehicleTrailsSliceRedux;

export const { reset, setUnitVehicleTrail, setTripsModeActive /* searchRoutesTrackerRefresh */ } = actions;

export default reducer;